/**
 * The box door over the relay (box hosts spec §6): for a host whose runner dials out to us (boat, and
 * the person's own computer), the server asks the runner to read, write, list or run something in its
 * own workspace, and the runner answers once, echoing the requestId (awaitSandboxEvent). Server ↔
 * runner only, never forwarded to a browser.
 *
 * Paths on the wire are relative to the runner's workspace: the server strips Blaxel's
 * `/app/workspace` before a frame goes out, and the runner joins what it gets to its own WORKING_DIR
 * and confines it again. Small content rides inline as base64, a chunk at a time; a big write never
 * crosses the server (write_from_file: the runner pulls the bytes from Storage by file id).
 */
import { z } from 'zod';

/** The most bytes one frame carries inline: a read's chunk, or a whole inline write. */
export const BOX_CHUNK_BYTES = 4 * 1024 * 1024;
/** The longest base64 one chunk spells, padding included: a read answer never carries more. */
export const BOX_CHUNK_BASE64_CHARS = Math.ceil(BOX_CHUNK_BYTES / 3) * 4;
/** The biggest file the server reads through the door, page by page. */
export const BOX_READ_MAX_BYTES = 64 * 1024 * 1024;
export const BOX_LIST_MAX_ENTRIES = 5_000;
export const BOX_EXEC_MAX_OUTPUT_BYTES = 1024 * 1024;
export const BOX_EXEC_DEFAULT_TIMEOUT_MS = 60_000;
export const BOX_EXEC_MAX_TIMEOUT_MS = 300_000;
export const BOX_LOGS_MAX_LINES = 2_000;
/** One frame's round trip: the server waits this long for an answer, and the runner holds the box this long plus a margin. */
export const BOX_FRAME_TIMEOUT_MS = 30_000;
/**
 * What a runner that answers these frames declares in its AGENT_READY capabilities, on every host (the
 * person's own computer included). The server asks a runner only when it declared this, so an older runner
 * is told apart at once (BoxNoHandlerError) instead of after a frame's whole timeout.
 */
export const BOX_DOOR_CAPABILITY = 'box-door-v1';
/**
 * What a runner declares when it starts a `detach` exec so the command outlives the request (Plan 3c), on a cloud
 * host only. An older runner ignores `detach` and would run the command as a bounded one, killing it at its timeout,
 * so the server never sends it `detach` without this.
 */
export const BOX_EXEC_DETACH_CAPABILITY = 'box-exec-detach-v1';

/** A path under the workspace: never absolute, never a `..` segment. '' is the workspace itself. */
export const BoxPathSchema = z.string().max(4096).refine(
  (p) => !p.startsWith('/') && !p.split('/').some((seg) => seg === '..'),
  { message: 'a box path is relative to the workspace and never climbs out of it' },
);
const requestId = z.string().min(1);

/** Server → runner (BOX_FS_REQUEST): apps/server services/box/dial-way.ts. */
export const BoxFsRequestPayloadSchema = z.discriminatedUnion('op', [
  z.object({ requestId, op: z.literal('read'), path: BoxPathSchema, offset: z.number().int().nonnegative(), length: z.number().int().positive().max(BOX_CHUNK_BYTES) }),
  z.object({ requestId, op: z.literal('write'), path: BoxPathSchema, bytesBase64: z.string() }),
  z.object({ requestId, op: z.literal('write_from_file'), path: BoxPathSchema, fileId: z.string().uuid() }),
  z.object({ requestId, op: z.literal('list'), path: BoxPathSchema }),
  z.object({ requestId, op: z.literal('stat'), path: BoxPathSchema }),
  z.object({ requestId, op: z.literal('mkdir'), path: BoxPathSchema }),
  z.object({ requestId, op: z.literal('remove'), path: BoxPathSchema, recursive: z.boolean() }),
  z.object({ requestId, op: z.literal('copy'), from: BoxPathSchema, to: BoxPathSchema }),
]);
export type BoxFsRequestPayload = z.infer<typeof BoxFsRequestPayloadSchema>;

export const BOX_FS_ERRORS = ['not_found', 'outside_workspace', 'too_large', 'is_directory', 'not_a_directory', 'not_on_this_host', 'pull_failed', 'failed'] as const;
export type BoxFsError = (typeof BOX_FS_ERRORS)[number];
export const BoxEntrySchema = z.object({ name: z.string(), kind: z.enum(['file', 'dir', 'other']), size: z.number().int().nonnegative(), mtimeMs: z.number() });
export type BoxEntry = z.infer<typeof BoxEntrySchema>;

/** Runner → server (BOX_FS_RESULT): apps/sandbox handlers/box-door.ts; every request answers once. */
export const BoxFsResultPayloadSchema = z.object({
  requestId,
  ok: z.boolean(),
  error: z.enum(BOX_FS_ERRORS).optional(),
  message: z.string().optional(),
  /** read: this chunk, the whole file's size, and whether this chunk ends the file. stat: size and kind. */
  bytesBase64: z.string().max(BOX_CHUNK_BASE64_CHARS).optional(),
  size: z.number().int().nonnegative().optional(),
  eof: z.boolean().optional(),
  kind: z.enum(['file', 'dir', 'other']).optional(),
  mtimeMs: z.number().optional(),
  /** list: at most BOX_LIST_MAX_ENTRIES, and whether more were left out. */
  entries: z.array(BoxEntrySchema).max(BOX_LIST_MAX_ENTRIES).optional(),
  truncated: z.boolean().optional(),
  /** write, write_from_file: bytes written. */
  written: z.number().int().nonnegative().optional(),
});
export type BoxFsResultPayload = z.infer<typeof BoxFsResultPayloadSchema>;

/**
 * Server → runner (BOX_EXEC_REQUEST): one shell command in the workspace, bounded in time and output.
 * Not strict: a later field is added as optional, and an older runner ignores it.
 */
export const BoxExecRequestPayloadSchema = z.object({
  requestId,
  command: z.string().min(1).max(100_000),
  /** Where to run, relative to the workspace; absent: the workspace itself. */
  cwd: BoxPathSchema.optional(),
  timeoutMs: z.number().int().positive().max(BOX_EXEC_MAX_TIMEOUT_MS),
  maxOutputBytes: z.number().int().positive().max(BOX_EXEC_MAX_OUTPUT_BYTES),
  /** Plan 3c: start the command so it outlives the request (a transient systemd user unit), and answer once it has
   *  started. Sent only to a runner that declared BOX_EXEC_DETACH_CAPABILITY. */
  detach: z.boolean().optional(),
  /** With detach: the unit's name after `vestra-`, lowercase letters, digits and dashes. */
  name: z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/).optional(),
});
export const BOX_EXEC_ERRORS = ['not_on_this_host', 'outside_workspace', 'timeout', 'failed'] as const;
/** Runner → server (BOX_EXEC_RESULT): an exit code is an answer (ok); `error` only when it did not run, or was cut. */
export const BoxExecResultPayloadSchema = z.object({
  requestId,
  ok: z.boolean(),
  error: z.enum(BOX_EXEC_ERRORS).optional(),
  message: z.string().optional(),
  exitCode: z.number().int(),
  stdout: z.string(),
  stderr: z.string(),
  truncated: z.boolean(),
  durationMs: z.number().int().nonnegative(),
});
/** Server → runner (BOX_LOGS_REQUEST): the runner's own last log lines. */
export const BoxLogsRequestPayloadSchema = z.object({ requestId, lines: z.number().int().positive().max(BOX_LOGS_MAX_LINES) });
/** Runner → server (BOX_LOGS_RESULT). */
export const BoxLogsResultPayloadSchema = z.object({
  requestId,
  ok: z.boolean(),
  error: z.enum(['not_on_this_host', 'failed']).optional(),
  logs: z.string(),
  status: z.string(),
});
export type BoxExecRequestPayload = z.infer<typeof BoxExecRequestPayloadSchema>;
export type BoxExecResultPayload = z.infer<typeof BoxExecResultPayloadSchema>;
export type BoxLogsRequestPayload = z.infer<typeof BoxLogsRequestPayloadSchema>;
export type BoxLogsResultPayload = z.infer<typeof BoxLogsResultPayloadSchema>;
